import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Box,
    TextField,
    Typography,
} from "@mui/material";

import WizardWrapper from './WizardWrapper';
import ConfirmDialog from './ConfirmDialog';
import CopyButton from './CopyButton';

import { useAppState } from './AppStateContext';
import { isGeneratedEmail } from './helpers';

const EditStudent = ({ studentData, classId, open, onClose }) => {
    const { apiPut, apiPost, apiDelete } = useAppState();
    const navigate = useNavigate();

    const defaultUserInput = { email: '', name: '' };
    const [userInput, setUserInput] = useState(defaultUserInput);
    const [nameError, setNameError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
    const [confirmResetOpen, setConfirmResetOpen] = useState(false);
    const [newPassword, setNewPassword] = useState(null);
    const [isResetting, setIsResetting] = useState(false);

    useEffect(() => {
        if (studentData) {
            setUserInput({ name: studentData.name, email: studentData.email });
        }
    }, [studentData, open]);

    const handleClose = (event, reason) => {
        setUserInput(defaultUserInput);
        setNameError(false);
        setErrorMessage("");
        // The repopulating effect only touches name/email, so a password
        // from this session would otherwise leak into the next open.
        setNewPassword(null);
        setConfirmResetOpen(false);
        onClose(reason === 'created');
    };

    const handleInputChange = (e) => {
        setNameError(false);
        setErrorMessage("");
        setUserInput({
            ...userInput,
            [e.target.name]: e.target.value,
        });
    };

    const handleRemove = async () => {
        try {
            await apiDelete(`classes/${classId}/students/${encodeURIComponent(studentData.email)}`);
            // Leave the page of the removed student directly, instead of
            // letting the parent refetch it and react to the resulting 404.
            handleClose(null);
            navigate(`/classes/${classId}`);
        } catch (error) {
            setErrorMessage("An error occurred while trying to remove the student.");
        }
    };

    const handleReset = async () => {
        // Overlapping resets could display a password that a later response
        // already invalidated.
        if (isResetting) return;
        setIsResetting(true);
        setErrorMessage("");
        try {
            const response = await apiPost(`students/${encodeURIComponent(studentData.email)}/password-reset`);
            setNewPassword(response.data.password);
        } catch (error) {
            setErrorMessage("An error occurred while trying to reset the password.");
        } finally {
            setIsResetting(false);
        }
    };

    const handleSave = async () => {
        try {
            await apiPut(`students/${encodeURIComponent(studentData.email)}`, { name: userInput.name } );
            handleClose(null, 'created');
        } catch (error) {
            setErrorMessage("An error occurred while trying to modify the student.");
        }
    };

    return (
        <WizardWrapper open={open} onClose={handleClose} onBack={null} canGoBack={false}>
            <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h5">Edit Student</Typography>
                <Typography variant="body1" sx={{ mb: 3, pl: '100px', pr: '100px' }}>
                    Here you can change the student’s name, or remove them from the class.
                </Typography>
                <Box sx={{ display: 'block', marginBottom: '16px' }}>
                    <TextField
                        disabled
                        margin="dense"
                        name="email"
                        label={isGeneratedEmail(userInput.email) ? "Username" : "Email address"}
                        fullWidth
                        value={userInput.email}
                        sx={{ mt: 2, mb: 2, width: '300px' }}
                    />
                </Box>
                <Box sx={{ display: 'block', marginBottom: '16px' }}>
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        name="name"
                        label="Name of student"
                        fullWidth
                        value={userInput.name}
                        onChange={handleInputChange}
                        error={nameError !== false}
                        helperText={nameError}
                        sx={{ mt: 2, mb: 5, width: '300px' }}
                        autoComplete="off"
                    />
                </Box>
                {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
                {newPassword &&
                    <p>
                        New password: <span style={{ fontFamily: 'monospace' }}>{newPassword}</span>
                        {' '}<CopyButton text={newPassword} label="Copy password" />
                        <br />
                        Write it down now — it cannot be shown again.
                    </p>
                }
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, mb: 2 }}>
                    <Box sx={{ mr: 5 }}>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => setConfirmRemoveOpen(true)}
                        >
                            Remove
                        </Button>
                        {isGeneratedEmail(studentData?.email) &&
                            <Button
                                variant="outlined"
                                color="primary"
                                sx={{ ml: 2 }}
                                onClick={() => setConfirmResetOpen(true)}
                            >
                                Reset password
                            </Button>
                        }
                    </Box>
                    <Box>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ mr: 2 }}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{ mr: 2 }}
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Box>
            <ConfirmDialog
                open={confirmRemoveOpen}
                title="Remove student"
                message={`Do you really want to remove ${studentData?.name} from the class?`}
                confirmLabel="Remove"
                onConfirm={() => { setConfirmRemoveOpen(false); handleRemove(); }}
                onCancel={() => setConfirmRemoveOpen(false)}
            />
            <ConfirmDialog
                open={confirmResetOpen}
                title="Reset password"
                message={`Generate a new password for ${studentData?.name}? The old password will stop working.`}
                confirmLabel="Reset password"
                onConfirm={() => { setConfirmResetOpen(false); handleReset(); }}
                onCancel={() => setConfirmResetOpen(false)}
            />
        </WizardWrapper>
    );
};

export default EditStudent;
